import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  IconButton,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Input,
  VStack,
  Text,
  useToast,
  Badge,
  HStack,
  Tooltip,
  Avatar,
  AvatarGroup,
  Switch
} from '@chakra-ui/react';
import { FiEdit2, FiTrash2, FiPlus, FiUsers, FiShield } from 'react-icons/fi';
import { Select } from 'chakra-react-select';
import { collection, onSnapshot, addDoc, updateDoc, deleteDoc, doc, serverTimestamp, query, orderBy } from 'firebase/firestore';
import { db } from '../../../utils/init-firebase';
import { useAuth } from '../../../contexts/AuthContext';

/**
 * Logs an audit event for round robin configuration changes
 * @param {Object} params - Audit parameters
 * @param {string} params.action - Action type (round_robin_created, round_robin_updated, round_robin_deleted)
 * @param {Object} params.currentUser - Current user object
 * @param {Object} params.details - Action-specific details
 * @param {Object} params.beforeSnapshot - State before change
 * @param {Object} params.afterSnapshot - State after change
 * @param {string} params.entityId - Round robin ID
 */
async function logRoundRobinAudit({ action, currentUser, details, beforeSnapshot = null, afterSnapshot = null, entityId }) {
  try {
    const auditLogData = {
      action,
      actionLabel: {
        'round_robin_created': 'Round Robin Created',
        'round_robin_updated': 'Round Robin Updated',
        'round_robin_deleted': 'Round Robin Deleted',
        'round_robin_users_added': 'Users Added to Round Robin',
        'round_robin_users_removed': 'Users Removed from Round Robin',
      }[action] || action,
      userId: currentUser?.uid || 'unknown',
      userName: currentUser?.displayName || currentUser?.Name || currentUser?.email || 'Unknown User',
      details,
      beforeSnapshot,
      afterSnapshot,
      entityType: 'roundRobin',
      entityId,
      timestamp: serverTimestamp(),
      createdAt: new Date().toISOString(),
    };

    await addDoc(collection(db, 'auditLogs'), auditLogData);
  } catch (error) {
    console.error('Error logging round robin audit:', error);
    // Don't throw - audit logging should not break primary operations
  }
}

/**
 * Compares two arrays and returns added/removed items
 */
function compareUserArrays(beforeIds = [], afterIds = [], crmUsers = []) {
  const getUserName = (uid) => {
    const user = crmUsers.find(u => u.uid === uid);
    return user?.name || user?.Name || user?.email || uid;
  };

  const added = afterIds.filter(id => !beforeIds.includes(id));
  const removed = beforeIds.filter(id => !afterIds.includes(id));

  return {
    added: added.map(uid => ({ uid, name: getUserName(uid) })),
    removed: removed.map(uid => ({ uid, name: getUserName(uid) })),
    addedNames: added.map(uid => getUserName(uid)),
    removedNames: removed.map(uid => getUserName(uid)),
  };
}

export default function RoundRobinManager({ crmUsers }) {
  const { currentUser } = useAuth();
  const [roundRobins, setRoundRobins] = useState([]);
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [editingRR, setEditingRR] = useState(null);
  const [name, setName] = useState('');
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [checkStateLicenses, setCheckStateLicenses] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const toast = useToast();

  useEffect(() => {
    const q = query(collection(db, 'roundRobins'), orderBy('name'));
    const unsubscribe = onSnapshot(q, (snapshot) => {
      const rrList = [];
      snapshot.forEach((doc) => {
        rrList.push({ id: doc.id, ...doc.data() });
      });
      setRoundRobins(rrList);
    });
    return () => unsubscribe();
  }, []);

  const handleOpenModal = (rr = null) => {
    if (rr) {
      setEditingRR(rr);
      setName(rr.name);
      setCheckStateLicenses(rr.checkStateLicenses || false);
      const currentUsers = rr.userIds.map(uid => {
        const user = crmUsers.find(u => u.uid === uid);
        return user ? { label: user.name || user.Name || user.email, value: user.uid, photoURL: user.photoURL } : null;
      }).filter(Boolean);
      setSelectedUsers(currentUsers);
    } else {
      setEditingRR(null);
      setName('');
      setCheckStateLicenses(false);
      setSelectedUsers([]);
    }
    onOpen();
  };

  const handleSave = async () => {
    if (!name.trim()) {
      toast({ title: 'Name is required', status: 'error' });
      return;
    }
    if (selectedUsers.length === 0) {
        toast({ title: 'Select at least one user', status: 'error' });
        return;
    }

    setIsLoading(true);
    try {
      const userIds = selectedUsers.map(u => u.value);
      const userNames = selectedUsers.map(u => u.label);
      
      if (editingRR) {
        // Updating existing round robin
        const beforeUserIds = editingRR.userIds || [];
        const userChanges = compareUserArrays(beforeUserIds, userIds, crmUsers);
        
        await updateDoc(doc(db, 'roundRobins', editingRR.id), {
          name,
          userIds,
          checkStateLicenses,
          updatedAt: serverTimestamp()
        });

        // Log the update with detailed change information
        await logRoundRobinAudit({
          action: 'round_robin_updated',
          currentUser,
          details: {
            roundRobinName: name,
            previousName: editingRR.name,
            nameChanged: name !== editingRR.name,
            usersAdded: userChanges.addedNames,
            usersRemoved: userChanges.removedNames,
            usersAddedCount: userChanges.added.length,
            usersRemovedCount: userChanges.removed.length,
            totalUsersAfter: userIds.length,
            totalUsersBefore: beforeUserIds.length,
            licenseCheckChanged: checkStateLicenses !== (editingRR.checkStateLicenses || false),
            licenseCheckEnabled: checkStateLicenses,
          },
          beforeSnapshot: {
            name: editingRR.name,
            userIds: beforeUserIds,
            userNames: beforeUserIds.map(uid => {
              const user = crmUsers.find(u => u.uid === uid);
              return user?.name || user?.Name || user?.email || uid;
            }),
            checkStateLicenses: editingRR.checkStateLicenses || false,
            lastAssignedIndex: editingRR.lastAssignedIndex,
          },
          afterSnapshot: {
            name,
            userIds,
            userNames,
            checkStateLicenses,
          },
          entityId: editingRR.id,
        });

        toast({ title: 'Round Robin updated', status: 'success' });
      } else {
        // Creating new round robin
        const docRef = await addDoc(collection(db, 'roundRobins'), {
          name,
          userIds,
          checkStateLicenses,
          lastAssignedIndex: -1, // Start before the first user so next is 0
          skippedUsers: [], // Initialize empty queue for license-skipped users
          createdAt: serverTimestamp(),
          updatedAt: serverTimestamp()
        });

        // Log the creation
        await logRoundRobinAudit({
          action: 'round_robin_created',
          currentUser,
          details: {
            roundRobinName: name,
            usersAdded: userNames,
            usersAddedCount: userIds.length,
            licenseCheckEnabled: checkStateLicenses,
          },
          afterSnapshot: {
            name,
            userIds,
            userNames,
            checkStateLicenses,
            lastAssignedIndex: -1,
          },
          entityId: docRef.id,
        });

        toast({ title: 'Round Robin created', status: 'success' });
      }
      onClose();
    } catch (error) {
      console.error(error);
      toast({ title: 'Error saving Round Robin', description: error.message, status: 'error' });
    } finally {
      setIsLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this Round Robin?')) return;
    
    // Find the round robin being deleted for audit logging
    const rrToDelete = roundRobins.find(rr => rr.id === id);
    
    try {
      await deleteDoc(doc(db, 'roundRobins', id));

      // Log the deletion with full snapshot of what was deleted
      if (rrToDelete) {
        const userNames = (rrToDelete.userIds || []).map(uid => {
          const user = crmUsers.find(u => u.uid === uid);
          return user?.name || user?.Name || user?.email || uid;
        });

        await logRoundRobinAudit({
          action: 'round_robin_deleted',
          currentUser,
          details: {
            roundRobinName: rrToDelete.name,
            usersRemoved: userNames,
            usersRemovedCount: userNames.length,
            licenseCheckWasEnabled: rrToDelete.checkStateLicenses || false,
            lastAssignedIndex: rrToDelete.lastAssignedIndex,
          },
          beforeSnapshot: {
            name: rrToDelete.name,
            userIds: rrToDelete.userIds || [],
            userNames,
            checkStateLicenses: rrToDelete.checkStateLicenses || false,
            lastAssignedIndex: rrToDelete.lastAssignedIndex,
            skippedUsers: rrToDelete.skippedUsers || [],
          },
          entityId: id,
        });
      }

      toast({ title: 'Round Robin deleted', status: 'success' });
    } catch (error) {
      toast({ title: 'Error deleting', description: error.message, status: 'error' });
    }
  };

  const userOptions = crmUsers.map(user => ({
    label: user.name || user.Name || user.email,
    value: user.uid,
    photoURL: user.photoURL
  }));

  const getUserDetails = (uid) => {
      return crmUsers.find(u => u.uid === uid) || {};
  }

  return (
    <Box p={6} bg="white" borderRadius="lg" shadow="sm">
      <HStack justify="space-between" mb={6}>
        <VStack align="start" spacing={1}>
          <Text fontSize="lg" fontWeight="bold">Round Robin Configurations</Text>
          <Text fontSize="sm" color="gray.500">Manage automatic lead assignment rules</Text>
        </VStack>
        <Button leftIcon={<FiPlus />} colorScheme="blue" onClick={() => handleOpenModal()}>
          Create Round Robin
        </Button>
      </HStack>

      <Table variant="simple">
        <Thead>
          <Tr>
            <Th>Name (Webhook Identifier)</Th>
            <Th>Users in Rotation</Th>
            <Th>License Check</Th>
            <Th>Last Index</Th>
            <Th>Actions</Th>
          </Tr>
        </Thead>
        <Tbody>
          {roundRobins.map((rr) => (
            <Tr key={rr.id}>
              <Td fontWeight="medium">
                <Badge colorScheme="purple" fontSize="0.9em" px={2} py={1} borderRadius="md" textTransform="none">
                    {rr.name}
                </Badge>
              </Td>
              <Td>
                <HStack spacing={3}>
                    <Badge colorScheme="blue" variant="subtle">
                        {rr.userIds?.length || 0} Users
                    </Badge>
                    <AvatarGroup size="sm" max={5}>
                        {rr.userIds?.map(uid => {
                            const user = getUserDetails(uid);
                            return (
                                <Avatar 
                                    key={uid} 
                                    name={user.name || user.Name} 
                                    src={user.photoURL} 
                                />
                            );
                        })}
                    </AvatarGroup>
                </HStack>
              </Td>
              <Td>
                <Tooltip label={rr.checkStateLicenses ? 'Only assigns to users licensed in lead state' : 'Assigns without checking licenses'}>
                  <Badge 
                    colorScheme={rr.checkStateLicenses ? 'green' : 'gray'} 
                    variant={rr.checkStateLicenses ? 'solid' : 'subtle'}
                    fontSize="xs"
                  >
                    <HStack spacing={1}>
                      <FiShield size={10} />
                      <Text>{rr.checkStateLicenses ? 'On' : 'Off'}</Text>
                    </HStack>
                  </Badge>
                </Tooltip>
              </Td>
              <Td>{rr.lastAssignedIndex ?? '-'}</Td>
              <Td>
                <HStack spacing={2}>
                  <IconButton
                    icon={<FiEdit2 />}
                    aria-label="Edit"
                    size="sm"
                    variant="ghost"
                    onClick={() => handleOpenModal(rr)}
                  />
                  <IconButton
                    icon={<FiTrash2 />}
                    aria-label="Delete"
                    size="sm"
                    colorScheme="red"
                    variant="ghost"
                    onClick={() => handleDelete(rr.id)}
                  />
                </HStack>
              </Td>
            </Tr>
          ))}
          {roundRobins.length === 0 && (
            <Tr>
                <Td colSpan={5} textAlign="center" py={8} color="gray.500">
                    No Round Robins configured. Create one to get started.
                </Td>
            </Tr>
          )}
        </Tbody>
      </Table>

      <Modal isOpen={isOpen} onClose={onClose} size="lg">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>{editingRR ? 'Edit Round Robin' : 'Create Round Robin'}</ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl isRequired>
                <FormLabel>Name (Webhook Identifier)</FormLabel>
                <Input 
                    value={name} 
                    onChange={(e) => setName(e.target.value)} 
                    placeholder="e.g. facebook_leads_rr"
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                    This name must match the <code>roundrobinName</code> field in your webhook payload.
                </Text>
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Assign Users</FormLabel>
                <Select
                  isMulti
                  options={userOptions}
                  value={selectedUsers}
                  onChange={setSelectedUsers}
                  placeholder="Select users..."
                  closeMenuOnSelect={false}
                />
              </FormControl>

              <FormControl display="flex" alignItems="center" pt={2}>
                <HStack justify="space-between" w="full">
                  <VStack align="start" spacing={0}>
                    <FormLabel mb={0} fontWeight="medium">
                      Check State Licenses before assigning?
                    </FormLabel>
                    <Text fontSize="xs" color="gray.500">
                      When enabled, leads will only be assigned to users licensed in the lead's state.
                      If no one is licensed, the lead will remain unassigned.
                    </Text>
                  </VStack>
                  <Switch
                    colorScheme="green"
                    size="lg"
                    isChecked={checkStateLicenses}
                    onChange={(e) => setCheckStateLicenses(e.target.checked)}
                  />
                </HStack>
              </FormControl>
            </VStack>
          </ModalBody>
          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onClose}>Cancel</Button>
            <Button colorScheme="blue" onClick={handleSave} isLoading={isLoading}>
              Save Configuration
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>
    </Box>
  );
}

